import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import {
  type KnowledgeBuilderAssistantListItem,
  type CreateKnowledgeBuilderAssistantRequestBody,
  type CreateKnowledgeBuilderAssistantResponseBody,
  type KnowledgeBuilderSourceListItem,
  type ListKnowledgeBuilderAssistantsResponse,
  type ListKnowledgeBuilderSourcesResponse,
  type PaginatedResponse,
  Permissions,
  type UploadKnowledgeBuilderSourceResponseBody,
} from "common";

async function fetchKnowledgeAssistantsList(
  take: number,
  skip: number,
  searchKeyword?: string
): Promise<PaginatedResponse<KnowledgeBuilderAssistantListItem>> {
  try {
    const params: Record<string, string | number> = { take, skip };
    if (searchKeyword?.trim()) params.searchKeyword = searchKeyword.trim();

    const res = await axios.get<ListKnowledgeBuilderAssistantsResponse>(
      "/api/v1/cortex/knowledge-builder/assistants",
      { params }
    );

    return {
      data: res.data?.data ?? [],
      total: res.data?.total ?? 0,
      skip: res.data?.skip ?? skip,
      take: res.data?.take ?? take,
      pageTotal: res.data?.pageTotal ?? (res.data?.data ?? []).length,
    };
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export function useKnowledgeAssistantsList(
  page: number = 0,
  pageSize: number = 10,
  _sort: Array<{ id: string; desc: boolean }> = [],
  filter: Array<{ id: string; value: unknown }> = [],
  enabled: boolean = true
): UseQueryResult<PaginatedResponse<KnowledgeBuilderAssistantListItem>, Error> {
  const skip = page * pageSize;
  const take = pageSize;
  const searchKeyword = filter.find(f => f.id === "name")?.value as
    | string
    | undefined;

  return useQuery({
    queryKey: [
      "/api/v1/cortex/knowledge-builder/assistants",
      take,
      skip,
      searchKeyword,
    ],
    queryFn: () => fetchKnowledgeAssistantsList(take, skip, searchKeyword),
    retry: false,
    enabled,
  });
}

async function createKnowledgeAssistant(payload: {
  name: string;
  model: string;
  prompt: string | null;
  perms: Permissions;
  groupId?: number | null;
  writeGroupIds?: number[];
}): Promise<CreateKnowledgeBuilderAssistantResponseBody> {
  try {
    const res = await axios.post<CreateKnowledgeBuilderAssistantResponseBody>(
      "/api/v1/cortex/knowledge-builder/assistants",
      payload
    );
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export function useCreateAssistant(): UseMutationResult<
  CreateKnowledgeBuilderAssistantResponseBody,
  Error,
  CreateKnowledgeBuilderAssistantRequestBody
> {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: CreateKnowledgeBuilderAssistantRequestBody) =>
      createKnowledgeAssistant({
        ...payload,
        prompt: payload.prompt?.trim() || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/cortex/knowledge-builder/assistants"],
      });
      setNotification({
        id: "knowledge-builder-assistant-created",
        message: "Assistant created successfully",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: () => {
      setNotification({
        id: "knowledge-builder-assistant-create-error",
        message: "Failed to create assistant",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
}

async function deleteKnowledgeAssistant(assistantId: number): Promise<void> {
  try {
    await axios.delete(
      `/api/v1/cortex/knowledge-builder/assistants/${assistantId}`
    );
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export function useDeleteKnowledgeAssistant(): UseMutationResult<
  void,
  Error,
  number
> {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: deleteKnowledgeAssistant,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/cortex/knowledge-builder/assistants"],
      });
      setNotification({
        id: "knowledge-builder-assistant-deleted",
        message: "Assistant deleted",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: () => {
      setNotification({
        id: "knowledge-builder-assistant-delete-error",
        message: "Failed to delete assistant",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
}

async function fetchKnowledgeSources(
  assistantId: number
): Promise<ListKnowledgeBuilderSourcesResponse> {
  try {
    const res = await axios.get<ListKnowledgeBuilderSourcesResponse>(
      `/api/v1/cortex/knowledge-builder/assistants/${assistantId}/sources`
    );

    return res.data ?? [];
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export function useKnowledgeSources(
  assistantId: number | null,
  enabled: boolean = true
): UseQueryResult<KnowledgeBuilderSourceListItem[], Error> {
  return useQuery({
    queryKey: [
      "/api/v1/cortex/knowledge-builder/assistants",
      assistantId,
      "sources",
    ],
    queryFn: () => fetchKnowledgeSources(assistantId!),
    enabled: !!assistantId && enabled,
    retry: false,
  });
}

async function uploadKnowledgeSource(
  assistantId: number,
  file: File
): Promise<UploadKnowledgeBuilderSourceResponseBody> {
  if (!(file instanceof File)) {
    throw new Error("Invalid file. Please re-select the file and try again.");
  }

  const formData = new FormData();
  formData.append("file", file);

  const res = await axios.post(
    `/api/v1/cortex/knowledge-builder/assistants/${assistantId}/sources`,
    formData,
    {
      // Our shared axios instance defaults to JSON content-type; remove it for FormData uploads
      transformRequest: (data, headers) => {
        if (headers && typeof headers === "object") {
          delete (headers as Record<string, unknown>)["Content-Type"];
        }
        return data;
      },
    }
  );

  if (res.data?.status === "failed") {
    const message =
      res.data?.errorMessage?.trim() ||
      "File uploaded but ingestion failed. Please try a different file format.";
    throw new Error(message);
  }

  return res.data;
}

export function useUploadKnowledgeSource(): UseMutationResult<
  UploadKnowledgeBuilderSourceResponseBody,
  Error,
  { assistantId: number; file: File }
> {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: ({ assistantId, file }) =>
      uploadKnowledgeSource(assistantId, file),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          "/api/v1/cortex/knowledge-builder/assistants",
          variables.assistantId,
          "sources",
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/cortex/knowledge-builder/assistants"],
      });
    },
    onError: () => {
      setNotification({
        id: "knowledge-builder-source-upload-error",
        message: "Failed to upload knowledge source",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
}

async function deleteKnowledgeSource(sourceId: number): Promise<void> {
  await axios.delete(`/api/v1/cortex/knowledge-builder/sources/${sourceId}`);
}

export function useDeleteKnowledgeSource(): UseMutationResult<
  void,
  Error,
  { assistantId: number; sourceId: number }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sourceId }) => deleteKnowledgeSource(sourceId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          "/api/v1/cortex/knowledge-builder/assistants",
          variables.assistantId,
          "sources",
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/cortex/knowledge-builder/assistants"],
      });
    },
  });
}
